/**
 * 購入数量制限の動的更新
 *
 * PubSubイベント（cartUpdate）を購読し、カート追加/数量変更後に
 * 数量ピッカーのmax値、カートボタンの状態、警告メッセージを動的に更新する。
 *
 * cartUpdateイベントのcartDataはadd/change直後のレスポンスで、
 * itemsが含まれない場合があるため、/cart.jsを別途取得して正確な数量を把握する。
 */
(function () {
  'use strict';

  /**
   * 商品ページからproduct_idを取得する
   * @returns {number|null} 商品ID
   */
  function getProductId() {
    const product_info = document.querySelector('product-info');
    if (product_info && product_info.dataset.productId) {
      return parseInt(product_info.dataset.productId, 10);
    }
    return null;
  }

  /**
   * カート更新イベントハンドラ
   * イベントデータにitemsがある場合はそのまま使用、なければ/cart.jsから取得
   */
  function handleCartUpdate(event) {
    if (event && event.cartData && event.cartData.items) {
      processCartItems(event.cartData.items);
    } else {
      // /cart.jsからフルカートデータを取得
      fetchCartAndUpdate();
    }
  }

  /**
   * /cart.jsからカートデータを取得して更新
   */
  function fetchCartAndUpdate() {
    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        processCartItems(cart.items || []);
      })
      .catch(function (err) {
        console.error('購入制限: カートデータ取得エラー', err);
      });
  }

  /**
   * カートアイテムを処理して商品ページ・カートページのUIを更新
   */
  function processCartItems(cart_items) {
    updateProductPageLimits(cart_items);
    updateCartPageCheckout(cart_items);
  }

  /**
   * 同一商品のカート内合計数量を計算する（product_idベースで照合）
   * @param {Array} cart_items カートアイテム配列
   * @param {number} product_id 商品ID
   * @returns {number} カート内合計数量
   */
  function getCartQuantityByProductId(cart_items, product_id) {
    let cart_qty = 0;
    cart_items.forEach(function (item) {
      if (item.product_id === product_id) {
        cart_qty += item.quantity;
      }
    });
    return cart_qty;
  }

  /**
   * 商品ページの購入制限UIを更新
   */
  function updateProductPageLimits(cart_items) {
    const quantity_input = document.querySelector('product-info .quantity__input');
    if (!quantity_input) return;

    const purchase_limit = parseInt(quantity_input.dataset.purchaseLimit, 10);
    if (!purchase_limit || purchase_limit <= 0) return;

    // product_idベースでカート内数量を計算
    const product_id = getProductId();
    let cart_qty = 0;
    if (product_id) {
      cart_qty = getCartQuantityByProductId(cart_items, product_id);
    }

    // 残りの購入可能数量を計算
    const remaining = Math.max(0, purchase_limit - cart_qty);
    const min_val = parseInt(quantity_input.min, 10) || 1;

    // 数量ピッカーのmax値を更新（min_val未満にはしない）
    // data-maxにはDawn標準product-info.jsのsetQuantityBoundriesが
    // data-max - data-cart-quantity で再計算するため、
    // 結果が常にmin_val以上になるようmax(purchase_limit, cart_qty + min_val)を設定
    const data_max_for_dawn = Math.max(purchase_limit, cart_qty + min_val);
    const effective_max = Math.max(remaining, min_val);
    quantity_input.max = effective_max;
    quantity_input.dataset.max = data_max_for_dawn;
    if (remaining > 0) {
      if (parseInt(quantity_input.value, 10) > remaining) {
        quantity_input.value = min_val;
      }
    } else {
      // 購入上限到達: maxはmin_valを維持し、ボタンdisabledで制御
      quantity_input.value = min_val;
    }

    // カート内数量表示の更新
    quantity_input.dataset.cartQuantity = cart_qty;
    const cart_qty_label = document.querySelector('.quantity__rules-cart');
    if (cart_qty_label) {
      cart_qty_label.classList.toggle('hidden', cart_qty === 0);
      const cart_qty_span = cart_qty_label.querySelector('span:last-child');
      if (cart_qty_span) {
        cart_qty_span.textContent = '(カート: ' + cart_qty + '個)';
      }
    }

    // カートに追加するボタンの状態を更新
    const submit_button = document.querySelector('product-form button[type="submit"]');
    if (submit_button) {
      if (remaining <= 0) {
        submit_button.setAttribute('aria-disabled', 'true');
        submit_button.setAttribute('disabled', 'disabled');
      } else {
        // 残りがあり、在庫切れでなければボタンを有効化
        // ただし coming soon 状態の場合は有効化しない
        const product_form_el = document.querySelector('product-form');
        const is_coming_soon = product_form_el && product_form_el.dataset.comingSoon === 'true';
        if (!is_coming_soon) {
          const variant_input = document.querySelector('product-form input[name="id"]');
          if (variant_input && !variant_input.disabled) {
            submit_button.removeAttribute('aria-disabled');
            submit_button.removeAttribute('disabled');
          }
        }
      }
    }

    // 超過メッセージの表示切り替え
    const exceeded_msg = document.querySelector('.cm-purchase-limit-exceeded');
    if (exceeded_msg) {
      exceeded_msg.classList.toggle('hidden', cart_qty < purchase_limit);
    }

    // 追従カートの数量ピッカーとボタンも同期更新
    const sticky_qty = document.querySelector('.cm-sticky-cart__quantity-input');
    if (sticky_qty) {
      if (remaining > 0) {
        sticky_qty.max = remaining;
        if (parseInt(sticky_qty.value, 10) > remaining) {
          sticky_qty.value = Math.min(remaining, parseInt(sticky_qty.min, 10) || 1);
        }
      } else {
        sticky_qty.max = parseInt(sticky_qty.min, 10) || 1;
        sticky_qty.value = parseInt(sticky_qty.min, 10) || 1;
      }
    }
    const sticky_btn = document.querySelector('.cm-sticky-cart__add-to-cart');
    if (sticky_btn) {
      sticky_btn.disabled = remaining <= 0;
    }

    // Dawn標準バリデーション呼び出し
    const quantity_input_el = quantity_input.closest('quantity-input');
    if (quantity_input_el && typeof quantity_input_el.validateQtyRules === 'function') {
      quantity_input_el.validateQtyRules();
    }
  }

  /**
   * カートページのチェックアウトボタンを動的に更新
   * data-product-idで同一商品をグループ化し、合計数量がdata-purchase-limitを超えるかチェック
   */
  function updateCartPageCheckout() {
    const checkout_button = document.getElementById('checkout');
    if (!checkout_button) return;

    let has_exceeded = false;

    // data-purchase-limit属性を持つ数量入力要素を商品IDでグループ化
    const limit_inputs = document.querySelectorAll('.quantity__input[data-purchase-limit]');
    const product_totals = {};

    limit_inputs.forEach(function (input) {
      const limit = parseInt(input.dataset.purchaseLimit, 10);
      const product_id = input.dataset.productId;
      const current_qty = parseInt(input.value, 10) || 0;

      if (!limit || limit <= 0) return;

      if (product_id) {
        // 商品IDでグループ化して合算
        if (!product_totals[product_id]) {
          product_totals[product_id] = { total: 0, limit: limit };
        }
        product_totals[product_id].total += current_qty;
      } else {
        // product_idがない場合はアイテム単位でチェック
        if (current_qty > limit) {
          has_exceeded = true;
        }
      }
    });

    // 商品単位の合算チェック
    Object.keys(product_totals).forEach(function (pid) {
      if (product_totals[pid].total > product_totals[pid].limit) {
        has_exceeded = true;
      }
    });

    /* 同意チェックボックスの状態も考慮してボタンを制御 */
    const agreement_checkbox = document.getElementById('cmCartAgreementCheckbox');
    const is_agreed = agreement_checkbox ? agreement_checkbox.checked : false;
    checkout_button.disabled = has_exceeded || !is_agreed;

    const dynamic_checkout = document.querySelector('.cart__dynamic-checkout-buttons');
    if (dynamic_checkout) {
      dynamic_checkout.classList.toggle('hidden', has_exceeded || !is_agreed);
    }

    const warnings = document.querySelector('[data-purchase-limit-warnings]');
    if (warnings) {
      warnings.classList.toggle('hidden', !has_exceeded);
    }
  }

  /**
   * カートページのDOM変更を監視し、セクション再レンダリング後にチェックを実行
   */
  function setupCartPageObserver() {
    const cart_footer = document.getElementById('main-cart-footer');
    if (!cart_footer) return;

    // セクション再レンダリング後にチェックを実行
    const observer = new MutationObserver(function () {
      updateCartPageCheckout();
    });
    observer.observe(cart_footer, { childList: true, subtree: true });

    // カートアイテムの数量入力のchangeイベントも監視
    const cart_items_el = document.querySelector('cart-items');
    if (cart_items_el) {
      cart_items_el.addEventListener('change', function (event) {
        if (event.target.classList.contains('quantity__input')) {
          // 少し遅延してチェック（DOMの更新を待つ）
          setTimeout(updateCartPageCheckout, 100);
        }
      });
    }

    // 初回チェック
    updateCartPageCheckout();
  }

  // PubSubイベント購読
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
  }

  // カートページのDOM監視を初期化
  setupCartPageObserver();
})();
